/* Wrapper for common router calls */
import { Router, RouteLocationNormalizedLoaded } from 'vue-router'
import * as routerNames from '@/services/router/router.names'
import Permissions from '@/plugins/permissions/permissions'
import { DefaultFilterIDs } from '@/types'
import LocalStore from '@/helpers/localstore'
import config from '@/config'

export default new class RouteAssistant {
  /**
   * Navigation details that aren't part of a route's path (title, refNumber, from_all...) travel in history state,
   * since the router drops params a path doesn't declare.
   */
  public routeState(route: RouteLocationNormalizedLoaded, key: string): any {
    // history.state isn't reactive; reading the route makes computed properties re-run on navigation
    void route.fullPath
    return window.history.state?.[key]
  }

  private openInNewWindow(router: Router, route: string) {
    const routeData = router.resolve(route)
    window.open(routeData.href, '_blank')
  }

  /* Go to routes */
  public goToAccountRegistrationComplete(
    router: Router,
    donedoneToken: string,
    gAuthToken: string,
    microsoftToken: string,
    gl: string,
  ): void {
    router.push(
      this.getAccountRegistrationCompleteRoute(donedoneToken, gAuthToken, microsoftToken, gl),
    )
  }

  public goToDashboard(router: Router, accountId: number | string): void {
    router.push(this.getDashboardRoute(accountId))
  }

  public goToAllConversationsWithDefaultFilter(
    router: Router,
    accountId: number | string,
    defaultFilterId: number | string,
  ): void {
    router.push(this.getAllConversationsRoute(accountId, defaultFilterId, null))
  }

  public goToAllConversationsWithCustomFilter(
    router: Router,
    accountId: number | string,
    customFilterId: number | string,
  ): void {
    router.push(this.getAllConversationsRoute(accountId, null, customFilterId))
  }

  public goToAllTasksWithDefaultFilter(
    router: Router,
    accountId: number | string,
    defaultFilterId: number | string,
  ): void {
    router.push(this.getAllTasksRoute(accountId, defaultFilterId, null))
  }

  public goToAllTasksWithCustomFilter(
    router: Router,
    accountId: number | string,
    customFilterId: number | string,
  ): void {
    router.push(this.getAllTasksRoute(accountId, null, customFilterId))
  }

  public goToMailbox(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): void {
    router.push(this.getMailboxLandingRoute(accountId, mailboxId, title))
  }

  public goToMailboxListing(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): void {
    router.push(
      this.getMailboxRoute(accountId, mailboxId, title),
    )
  }

  public goToMailboxSettings(
    router: Router,
    accountId: number | string,
    mailboxId: number,
  ): void {
    router.push({
      name: routerNames.MAILBOX_SETTINGS_GENERAL_SETTINGS,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    })
  }

  public goToMailboxKanban(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): void {
    router.push(this.getMailboxKanbanRoute(accountId, mailboxId, title))
  }

  public goToMailboxWithDefaultFilter(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    defaultFilterId: number | string,
  ): void {
    router.push(
      this.getMailboxRoute(accountId, mailboxId, null, defaultFilterId),
    )
  }

  public goToMailboxWithCustomFilter(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    customFilterId: number | string,
  ): void {
    router.push(
      this.getMailboxRoute(accountId, mailboxId, null, null, customFilterId),
    )
  }

  // Routes to either project listing or kanban based on last visit...
  public goToProjectLanding(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
  ): void {
    router.push(
      this.getProjectLandingRoute(accountId, projectId, permissions, title),
    )
  }

  public goToProjectListing(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
  ): void {
    router.push(
      this.getProjectListingRoute(accountId, projectId, permissions, title),
    )
  }

  public goToProjectSettings(
    router: Router,
    accountId: number | string,
    projectId: number | string,
  ): void {
    router.push({
      name: routerNames.PROJECT_SETTINGS_GENERAL_SETTINGS,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
    })
  }

  public goToProjectListingWithDefaultFilter(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    defaultFilterId: number | string,
  ): void {
    router.push(
      this.getProjectListingRoute(
        accountId,
        projectId,
        permissions,
        null,
        defaultFilterId,
      ),
    )
  }

  public goToProjectListingWithCustomFilter(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    customFilterId: number | string,
  ): void {
    router.push(
      this.getProjectListingRoute(
        accountId,
        projectId,
        permissions,
        null,
        null,
        customFilterId,
      ),
    )
  }

  public goToProjectKanban(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
  ): void {
    router.push(
      this.getProjectKanbanRoute(accountId, projectId, permissions, title),
    )
  }

  public goToProjectKanbanWithDefaultFilter(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    defaultFilterId: number | string,
  ): void {
    router.push(
      this.getProjectKanbanRoute(
        accountId,
        projectId,
        permissions,
        null,
        defaultFilterId,
      ),
    )
  }

  public goToProjectKanbanWithCustomFilter(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    customFilterId: number | string,
  ): void {
    router.push(
      this.getProjectKanbanRoute(
        accountId,
        projectId,
        permissions,
        null,
        null,
        customFilterId,
      ),
    )
  }

  public goToConversation(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    conversationId: number | string,
    mailboxName?: string | null,
    refNumber?: number | string | null,
    title?: string | null,
    newWindow?: boolean,
    eventId?: number | string | null,
    fromAll?: boolean | string | null,
    fromMailboxHome?: boolean | string | null,
  ): void {
    const route = this.getConversationRoute(
      accountId,
      mailboxId,
      conversationId,
      mailboxName,
      refNumber,
      title,
      eventId,
      fromAll,
      fromMailboxHome,
    )

    if (newWindow) {
      this.openInNewWindow(router, route)
    } else {
      router.push(route)
    }
  }

  public goToConversationFromKanbanBoard(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    conversationId: number | string,
    refNumber?: number | string | null,
  ): void {
    const route = this.getConversationRoute(
      accountId,
      mailboxId,
      conversationId,
      null,
      refNumber,
      null,
      null,
      null,
      true,
    )
    router.push(route)
  }

  public goToTask(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    taskId: number | string,
    permissions: Permissions,
    projectName?: string | null,
    refNumber?: number | string | null,
    title?: string | null,
    newWindow?: boolean,
    eventId?: number | string | null,
    fromAll?: boolean | string | null,
    fromListing?: boolean | string | null,
    fromKanbanBoard?: boolean | string | null,
  ): void {
    const route = this.getTaskRoute(
      accountId,
      projectId,
      taskId,
      permissions,
      projectName,
      refNumber,
      title,
      eventId,
      fromAll,
      fromListing,
      fromKanbanBoard,
    )

    if (newWindow) {
      this.openInNewWindow(router, route)
    } else {
      router.push(route)
    }
  }

  public goToTaskFromKanbanBoard(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    taskId: number | string,
    permissions: Permissions,
    refNumber?: number | string | null,
  ): void {
    const route = this.getTaskRoute(
      accountId,
      projectId,
      taskId,
      permissions,
      null,
      refNumber,
      null,
      null,
      null,
      null,
      true,
    )
    router.push(route)
  }

  public goToCreateConversation(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    mailboxName?: string | null,
  ): void {
    router.push({
      name: routerNames.CONVERSATION_NEW,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        mailboxName,
      },
    })
  }

  public goToCreateTask(
    router: Router,
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    projectName?: string | null,
  ): void {
    const name: string = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_TASK_NEW
      : routerNames.TASK_NEW

    router.push({
      name,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      state: {
        projectName,
      },
    })
  }

  public goToAllTasks(router: Router, accountId: number | string): void {
    router.push({
      name: routerNames.ALL_TASKS,
      params: {
        accountId: accountId.toString(),
      },
    })
  }

  public goToAllConversations(
    router: Router,
    accountId: number | string,
  ): void {
    router.push({
      name: routerNames.ALL_CONVERSATIONS,
      params: {
        accountId: accountId.toString(),
      },
    })
  }

  public goToWorkflowsHome(
    router: Router,
    accountId: number | string,
  ): void {
    router.push(this.getWorkflowsRoute(accountId))
  }

  public goToWorkflowDetail(
    router: Router,
    accountId: number | string,
    workflowId: number | string,
  ): void {
    router.push(this.getWorkflowDetailRoute(accountId, workflowId))
  }

  public goToCalendar(
    router: Router,
    accountId: number | string,
    entityId?: number | string,
  ): void {
    router.push(this.getCalendarRoute(accountId, entityId))
  }

  public goToReports(
    accountId: number | string,
    entityId?: number | string,
  ): void {
    this.goToUrl(this.getReportsUrl(accountId, entityId))
  }

  public goToDevelopersHome(
    router: Router,
    accountId: number | string,
  ): void {
    router.push(this.getDevelopersHomeRoute(accountId))
  }

  public goToOutboundWebhooks(
    router: Router,
    accountId: number | string,
  ): void {
    router.push(this.getOutboundWebhooksRoute(accountId))
  }

  public goToTicketPortalListing(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getTicketPortalListingRoute(accountId, mailboxId))
  }

  public goToTicketPortalDetail(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
    ticketId: number | string,
  ): void {
    router.push(this.getTicketPortalDetailRoute(accountId, mailboxId, ticketId))
  }

  public goToPeople(router: Router, accountId: number | string): void {
    router.push(this.getPeopleRoute(accountId))
  }

  public goToGuests(router: Router, accountId: number | string): void {
    router.push(this.getGuestsRoute(accountId))
  }

  public goToProfile(accountId: number | string): void {
    this.goToUrl(this.getProfileUrl(accountId))
  }

  public goToAccountSettings(accountId: number | string): void {
    this.goToUrl(this.getAccountSettingsUrl(accountId))
  }

  public goToBillingInformation(accountId: number | string): void {
    this.goToUrl(this.getBillingInformationUrl(accountId))
  }

  public goToSignup(): void {
    this.goToUrl(this.getSignupUrl())
  }

  public goToLogin(): void {
    this.goToUrl(this.getLoginUrl())
  }

  public goToBase(): void {
    this.goToUrl(this.getBaseUrl())
  }

  public goToPersonDetail(
    router: Router,
    accountId: number | string,
    accountMembershipId: number | string,
  ): void {
    router.push(this.getPersonDetailRoute(accountId, accountMembershipId))
  }

  public goToGuestsDetail(
    router: Router,
    accountId: number | string,
    accountMembershipId: number | string,
  ): void {
    router.push(this.getGuestsDetailRoute(accountId, accountMembershipId))
  }

  public goToCompanyDetail(
    router: Router,
    accountId: number | string,
    companyId: number | string,
  ): void {
    router.push(this.getCompanyDetailRoute(accountId, companyId))
  }

  public goToCompanyGuestsDetail(
    router: Router,
    accountId: number | string,
    companyId: number | string,
  ): void {
    router.push(this.getCompanyGuestsDetailRoute(accountId, companyId))
  }

  public goToCreateProject(
    router: Router,
    accountId: number | string,
    workflowId?: number | string,
  ): void {
    router.push(this.getCreateProjectRoute(accountId, workflowId))
  }

  public goToCreateMailbox(
    router: Router,
    accountId: number | string,
    workflowId?: number | string,
  ): void {
    router.push(this.getCreateMailboxRoute(accountId, workflowId))
  }

  public goToGlobalCreateTask(
    router: Router,
    accountId: number | string,
  ): void {
    router.push(this.getGlobalCreateTaskRoute(accountId))
  }

  public goToCreatePeople(
    router: Router,
    accountId: number | string,
    companyId?: number | string,
  ): void {
    router.push(this.getCreatePeopleRoute(accountId, companyId))
  }

  public goToCreateGuests(
    router: Router,
    accountId: number | string,
    companyId?: number | string,
  ): void {
    router.push(this.getCreateGuestsRoute(accountId, companyId))
  }

  public goToCreateCompany(
    router: Router,
    accountId: number | string,
  ): void {
    router.push(this.getCreateCompanyRoute(accountId))
  }

  public goToCreateGuestsCompany(
    router: Router,
    accountId: number | string,
  ): void {
    router.push(this.getCreateGuestsCompanyRoute(accountId))
  }

  public goToMailboxVerificationIntroRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getMailboxVerificationIntroRoute(accountId, mailboxId))
  }

  public goToMailboxVerificationAutoForwardingRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(
      this.getMailboxVerificationAutoForwardingRoute(accountId, mailboxId),
    )
  }

  public goToMailboxVerificationAutoForwardingTestingRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(
      this.getMailboxVerificationAutoForwardingTestingRoute(
        accountId,
        mailboxId,
      ),
    )
  }

  public goToMailboxVerificationOutgoingEmailRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(
      this.getMailboxVerificationOutgoingEmailRoute(accountId, mailboxId),
    )
  }

  public goToMailboxSettingsAutoForwardingRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(
      this.getMailboxSettingsAutoForwardingRoute(accountId, mailboxId),
    )
  }

  public goToMailboxSettingsOfficeHoursRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getMailboxSettingsOfficeHoursRoute(accountId, mailboxId))
  }

  public goToMailboxSettingsOutgoingEmailRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getMailboxSettingsOutgoingEmailRoute(accountId, mailboxId))
  }

  public goToMailboxSettingsTicketPortalRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getMailboxSettingsTicketPortalRoute(accountId, mailboxId))
  }

  public goToMailboxSettingsAssignPeopleRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getMailboxSettingsAssignPeopleRoute(accountId, mailboxId))
  }

  public goToProjectSettingsAssignPeopleRoute(
    router: Router,
    accountId: number | string,
    projectId: number | string,
  ): void {
    router.push(this.getProjectSettingsAssignPeopleRoute(accountId, projectId))
  }

  public goToMailboxSettingsDirectConnectionRoute(
    router: Router,
    accountId: number | string,
    mailboxId: number | string,
  ): void {
    router.push(this.getMailboxSettingsDirectConnectionRoute(accountId, mailboxId))
  }

  public goToUrl(url: string, external?: boolean): void {
    if (external) {
      window.open(url)
    } else {
      window.location.href = url
    }
  }

  /* Routes */
  public getDashboardRoute(accountId: number | string): any {
    return {
      name: routerNames.DASHBOARD,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getAllTasksRoute(
    accountId: number | string,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ) {
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}
    return {
      name: routerNames.ALL_TASKS,
      params: {
        accountId: accountId.toString(),
      },
      query,
    }
  }

  public getAllConversationsRoute(
    accountId: number | string,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ) {
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}
    return {
      name: routerNames.ALL_CONVERSATIONS,
      params: {
        accountId: accountId.toString(),
      },
      query,
    }
  }

  public getMailboxRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const titleVal: string = title ? title : ''
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routerNames.MAILBOX_HOME,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query,
    }
  }

  public getMailboxKanbanRoute(
    accountId: number | number | string,
    mailboxId: number | number | string,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const titleVal: string = title ? title : ''
    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routerNames.MAILBOX_KANBAN,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query,
    }
  }

  /* Returns mailbox landing route based on defaulted view (listing or KB) */
  public getMailboxLandingRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
    defaultFilterId?: number | string | null,
  ) {
    if (LocalStore.isMailboxDefaultedToKanban(mailboxId)) {
      return this.getMailboxKanbanRoute(
        accountId,
        mailboxId,
        title,
        defaultFilterId,
      )
    }
    return this.getMailboxRoute(
      accountId,
      mailboxId,
      title,
      defaultFilterId,
    )
  }

  public getMailboxSettingsDirectConnectionRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_DIRECT_CONNECTION,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsAutoForwardingRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_AUTO_FORWARDING,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsAutoResponderRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_AUTO_RESPONDER,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsOfficeHoursRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_OFFICE_HOURS,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsOutgoingEmailRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_OUTGOING_EMAIL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsTicketPortalRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_TICKET_PORTAL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsConversationRulesRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_CONVERSATION_RULES,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getMailboxSettingsChecklistTemplateRoute(
    accountId: number | string,
    projectId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_SETTINGS_CHECKLIST_TEMPLATES,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getMailboxSettingsWorkflowAutomationsRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_SETTINGS_WORKFLOW_AUTOMATIONS,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      query: {},
    }
  }

  public getMailboxSettingsCustomFieldsRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_SETTINGS_CUSTOM_FIELDS,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      query: {},
    }
  }

  public getMailboxSettingsAssignPeopleRoute(
    accountId: number | string,
    mailboxId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.MAILBOX_SETTINGS_ASSIGN_PEOPLE,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  public getProjectSettingsAssignPeopleRoute(
    accountId: number | string,
    projectId: number | string,
    title?: string | null,
  ): any {
    const titleVal: string = title ? title : ''

    return {
      name: routerNames.PROJECT_SETTINGS_ASSIGN_PEOPLE,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      state: {
        title: titleVal,
      },
      query: {},
    }
  }

  /* Returns project landing route based on defaulted view (listing or KB) */
  public getProjectLandingRoute(
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
    defaultFilterId?: number | string | null,
  ) {
    if (LocalStore.isProjectDefaultedToKanban(projectId)) {
      return this.getProjectKanbanRoute(
        accountId,
        projectId,
        permissions,
        title,
        defaultFilterId,
      )
    }
    return this.getProjectListingRoute(
      accountId,
      projectId,
      permissions,
      title,
      defaultFilterId,
    )
  }

  /* Returns project listing route based on if project is sample */
  public getProjectListingRoute(
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const routeName: string = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_PROJECT_LISTING
      : routerNames.PROJECT_LISTING

    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routeName,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      state: {
        title: title ? title : '',
      },
      query,
    }
  }

  /* Returns project kanban route based on if project is sample */
  public getProjectKanbanRoute(
    accountId: number | string,
    projectId: number | string,
    permissions: Permissions,
    title?: string | null,
    defaultFilterId?: number | string | null,
    customFilterId?: number | string | null,
  ): any {
    const routeName = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_PROJECT_KANBAN
      : routerNames.PROJECT_KANBAN

    const query: any = defaultFilterId
      ? {
          default_filter_id: defaultFilterId,
        }
      : customFilterId
        ? {
            custom_filter_id: customFilterId,
          }
        : {}

    return {
      name: routeName,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      state: {
        title: title ? title : '',
      },
      query,
    }
  }

  public getProjectScheduleRoute(
    accountId: number | string,
    projectId: number | string,
  ): any {
    const routeName = routerNames.PROJECT_SCHEDULE
    return {
      name: routeName,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getProjectSettingsChecklistTemplateRoute(
    accountId: number | string,
    projectId: number | string,
  ): any {
    return {
      name: routerNames.PROJECT_SETTINGS_CHECKLIST_TEMPLATES,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getProjectSettingsWorkflowAutomationsRoute(
    accountId: number | string,
    projectId: number | string,
  ): any {
    return {
      name: routerNames.PROJECT_SETTINGS_WORKFLOW_AUTOMATIONS,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getProjectSettingsCustomFieldsRoute(
    accountId: number | string,
    projectId: number | string,
  ): any {
    return {
      name: routerNames.PROJECT_SETTINGS_CUSTOM_FIELDS,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
      },
      query: {},
    }
  }

  public getConversationRoute(
    accountId: number | string,
    mailboxId: number | string,
    conversationId: number | string,
    mailboxName: string | null,
    refNumber?: number | string | null,
    title?: string | null,
    eventId?: number | string | null,
    fromAll?: boolean | string | null,
    fromMailboxHome?: boolean | string | null,
  ): any {
    let titleVal: string = ''

    if (refNumber && title) {
      titleVal = `#${refNumber}: ${title}`
    }

    const route: any = {
      name: routerNames.CONVERSATION_DETAIL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
        childId: conversationId.toString(),
      },
      state: {
        title: titleVal,
        refNumber,
        mailboxName,
        from_all: fromAll,
        from_entity_home: fromMailboxHome,
      },
    }

    if (eventId) {
      route.hash = '#e' + eventId
    }

    return route
  }

  public getTaskRoute(
    accountId: number | string,
    projectId: number | string,
    taskId: number | string,
    permissions: Permissions,
    projectName?: string | null,
    refNumber?: number | string | null,
    title?: string | null,
    eventId?: number | string | null,
    fromAll?: boolean | string | null,
    fromListing?: boolean | string | null,
    fromKanbanBoard?: boolean | string | null,
  ): any {
    let titleVal: string = ''

    if (refNumber && title) {
      titleVal = `#${refNumber}: ${title}`
    }

    const name: string = permissions.isSampleInternalProjectID(projectId)
      ? routerNames.SAMPLE_TASK_DETAIL
      : routerNames.TASK_DETAIL

    const route: any = {
      name,
      params: {
        accountId: accountId.toString(),
        id: projectId.toString(),
        childId: taskId.toString(),
      },
      state: {
        title: titleVal,
        refNumber,
        projectName,
        from_all: fromAll,
        from_entity_home: fromListing,
        from_kanban_board: fromKanbanBoard,
      },
    }

    if (eventId) {
      route.hash = '#e' + eventId
    }

    return route
  }

  public getWorkflowsRoute(accountId: number | string): any {
    return {
      name: routerNames.WORKFLOW_HOME,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getWorkflowDetailRoute(
    accountId: number | string,
    workflowId: number | string,
  ): any {
    return {
      name: routerNames.WORKFLOW_DETAIL,
      params: {
        accountId: accountId.toString(),
        id: workflowId.toString(),
      },
    }
  }

  public getCalendarRoute(
    accountId: number | string,
    entityId?: number | string,
  ): any {
    if (entityId) {
      return {
        name: routerNames.CALENDAR,
        params: {
          accountId: accountId.toString(),
        },
        query: {
          entity_id: entityId,
        },
      }
    }
    return {
      name: routerNames.CALENDAR,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getPeopleRoute(accountId: number | string): any {
    return {
      name: routerNames.PEOPLE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getPersonDetailRoute(
    accountId: number | string,
    accountMembershipId: number | string,
  ): any {
    return {
      name: routerNames.PERSON_DETAIL,
      params: {
        accountId: accountId.toString(),
        accountMembershipId: accountMembershipId.toString(),
      },
    }
  }

  public getGuestsRoute(accountId: number | string): any {
    return {
      name: routerNames.GUESTS,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getGuestsDetailRoute(
    accountId: number | string,
    accountMembershipId: number | string,
  ): any {
    return {
      name: routerNames.GUESTS_DETAIL,
      params: {
        accountId: accountId.toString(),
        accountMembershipId: accountMembershipId.toString(),
      },
    }
  }

  public getCompanyDetailRoute(
    accountId: number | string,
    companyId: number | string,
  ): any {
    return {
      name: routerNames.COMPANY_DETAIL,
      params: {
        accountId: accountId.toString(),
        companyId: companyId.toString(),
      },
    }
  }

  public getCompanyGuestsDetailRoute(
    accountId: number | string,
    companyId: number | string,
  ): any {
    return {
      name: routerNames.COMPANY_GUESTS_DETAIL,
      params: {
        accountId: accountId.toString(),
        companyId: companyId.toString(),
      },
    }
  }

  public getCreateProjectRoute(
    accountId: number | string,
    workflowId?: number | string,
  ): any {
    return {
      name: routerNames.PROJECT_NEW,
      params: { accountId: accountId.toString() },
      query: {
        workflow_id: workflowId,
      },
    }
  }

  public getCreateMailboxRoute(
    accountId: number | string,
    workflowId?: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_NEW,
      params: { accountId: accountId.toString() },
      query: {
        workflow_id: workflowId,
      },
    }
  }

  public getGlobalCreateTaskRoute(accountId: number | string): any {
    return {
      name: routerNames.GLOBAL_TASK_NEW,
      params: { accountId: accountId.toString() },
    }
  }

  public getMailboxVerificationIntroRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_INTRO,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getMailboxVerificationAutoForwardingRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_AUTO_FORWARDING,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getMailboxVerificationAutoForwardingTestingRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_AUTO_FORWARDING_TESTING,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getMailboxVerificationOutgoingEmailRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.MAILBOX_VERIFICATION_OUTGOING_EMAIL,
      params: {
        accountId: accountId.toString(),
        id: mailboxId.toString(),
      },
    }
  }

  public getCreatePeopleRoute(
    accountId: number | string,
    companyId?: number | string,
  ): any {
    if (companyId) {
      return {
        name: routerNames.COMPANY_PEOPLE_CREATE,
        params: {
          accountId: accountId.toString(),
          companyId: companyId.toString(),
        },
      }
    }

    return {
      name: routerNames.PEOPLE_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getCreateGuestsRoute(
    accountId: number | string,
    companyId?: number | string,
  ): any {
    if (companyId) {
      // TODO: fix this
      return {
        name: routerNames.COMPANY_PEOPLE_CREATE,
        params: {
          accountId: accountId.toString(),
          companyId: companyId.toString(),
        },
      }
    }

    return {
      name: routerNames.GUESTS_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getCreateCompanyRoute(accountId: number | string): any {
    return {
      name: routerNames.COMPANY_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getCreateGuestsCompanyRoute(accountId: number | string): any {
    return {
      name: routerNames.COMPANY_GUESTS_CREATE,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getAccountRegistrationCompleteRoute(
    donedoneToken: string,
    gAuthToken: string,
    microsoftToken: string,
    gl: string,
  ): any {
    return {
      name: routerNames.ACCOUNT_REGISTRATION_COMPLETE,
      query: {
        donedone_token: donedoneToken,
        gauth_token: gAuthToken,
        microsoft_token: microsoftToken,
        _gl: gl,
      },
    }
  }

  public getDevelopersHomeRoute(accountId: number | string): any {
    return {
      name: routerNames.DEVELOPERS_HOME,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getOutboundWebhooksRoute(accountId: number | string): any {
    return {
      name: routerNames.DEVELOPERS_OUTBOUND_WEBHOOKS,
      params: {
        accountId: accountId.toString(),
      },
    }
  }

  public getTicketPortalListingRoute(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return {
      name: routerNames.TICKET_PORTAL_LISTING,
      params: {
        accountId,
        mailboxId,
      },
    }
  }

  public getTicketPortalDetailRoute(
    accountId: number | string,
    mailboxId: number | string,
    ticketId: number | string,
  ): any {
    return {
      name: routerNames.TICKET_PORTAL_DETAIL,
      params: {
        accountId,
        mailboxId,
        ticketId,
      },
    }
  }

  public getTicketPortalProfileRoute(): any {
    return {
      name: routerNames.TICKET_PORTAL_PROFILE,
    }
  }

  /* Non-app or direct app URLs */
  public getReportsUrl(
    accountId: number | string,
    entityId?: number | string,
  ): any {
    if (entityId) {
      return `${config.serverSideAppUrl}/app/${accountId}/reports?entity_id=${entityId}`
    }
    return `${config.serverSideAppUrl}/app/${accountId}/reports`
  }

  public getProfileUrl(accountId: number | string): any {
    return `${config.serverSideAppUrl}/app/${accountId}/account/profile`
  }

  public getAPIKeyUrl(accountId: number | string): any {
    return `${config.serverSideAppUrl}/app/${accountId}/account/api-key`
  }

  public getAccountSettingsUrl(accountId: number | string): any {
    return `${config.serverSideAppUrl}/app/${accountId}/account/settings`
  }

  public getBillingInformationUrl(accountId: number | string): any {
    return `${config.serverSideAppUrl}/app/${accountId}/account/billing-information`
  }

  public getAccountPickerUrl(): any {
    return `${config.serverSideAppUrl}/account-picker`
  }

  public getSignupUrl(): any {
    return `${config.serverSideAppUrl}/signup`
  }

  public getLoginUrl(): any {
    return `${config.serverSideAppUrl}/security/login`
  }

  public getBaseUrl(): any {
    return config.serverSideAppUrl
  }

  public getReissueUserRegistrationEmailUrl(oldToken: string): any {
    return `${config.serverSideAppUrl}/signup/registration/re-issue-email?old_token=${oldToken}`
  }

  public getWorkflowsUrl(accountId: number | string): any {
    return `${config.serverSideAppUrl}/app/${accountId}/workflows`
  }

  public getWorkflowsCreateUrl(accountId: number | string): any {
    return `${config.serverSideAppUrl}/app/${accountId}/workflows/create`
  }

  public getMailboxSetupAutoForwardingUrl(
    accountId: number | string,
    mailboxId: number | string,
  ): any {
    return `${window.location.origin}/${accountId}/mailbox/${mailboxId}/setup/auto-forwarding`
  }

  public getProjectSetupAssignPeopleUrl(
    accountId: number | string,
    projectId: number | string,
  ): any {
    return `${window.location.origin}/${accountId}/project/${projectId}/setup/assign-people`
  }

  public getRegistrationAccountAddMembersUrl(
    accountId: number | string,
    email: string,
  ): any {
    return `${window.location.origin}/${accountId}/registration/add-members?email=${encodeURIComponent(email)}`
  }
}()
